import { AppModule } from "@jugl-web/domain-resources/common/types";
import { BoardTemplatesModal } from "@jugl-web/domain-resources/tasks/components/BoardTemplatesModal";
import { useTaskBoardsQuery } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardsQuery";
import { hasRoleAccessToModule } from "@jugl-web/domain-resources/users/utils/hasRoleAccessToModule";
import {
  ExpandableSearchBar,
  InteractiveContainer,
  Menu,
  MultiSectionLayout,
  Popover,
  SpotlightTooltip,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { useIsLargeOrBelow } from "@jugl-web/utils/hooks/useMediaQuery";
import { useSpotlight } from "@jugl-web/utils/hooks/useSpotlight";
import { CREATE_BOARD_SPOTLIGHT_KEY } from "@jugl-web/utils/storage";
import { useMe } from "@web-src/modules/auth/hooks/useMe";
import { ResizableSidebarWrapper } from "@web-src/modules/common/components/ResizableSidebar";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useFeaturesShowcaseModal } from "@web-src/modules/entities/components/FeaturesShowcaseModal/useFeaturesShowcaseModal";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { SidebarOverlay } from "@web-src/modules/navigation/components/SidebarOverlay";
import { useCurrentPageNavConfig } from "@web-src/modules/navigation/hooks/useCurrentPageNavConfig";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useSidebarNav } from "@web-src/modules/navigation/providers/SidebarNavProvider";
import {
  NewBoardDialog,
  NewBoardDialogHandle,
} from "@web-src/modules/tasks/components/NewBoardDialog";
import { useTheme } from "@web-src/themes/useTheme";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { ReactComponent as ChevronRightIcon } from "./assets/chevron-right.svg";
import { BoardListSkeleton } from "./components/BoardListSkeleton";
import { Divider } from "./components/Divider";
import { EntitiesUpdatesPopup } from "./components/EntitiesUpdatesPopup";
import { SidebarItemComponent } from "./components/SidebarItemComponent";
import { SidebarLinkButton } from "./components/SidebarLinkButton";
import { SIDEBAR_CONSTANTS } from "./consts";
import styles from "./HomeSidebar.module.css";
import { useEntitiesUpdatesPopup } from "./hooks/useEntitiesUpdatesPopup";
import { useHomeSidebar } from "./hooks/useHomeSidebar";
import { SidebarItem } from "./types";

import { ReactComponent as CreateBoardFromTemplateIcon } from "./assets/board-menu-1.svg";
import { ReactComponent as CreateBoardFromScratchIcon } from "./assets/board-menu-2.svg";

import { ReactComponent as MoreIconBlue } from "./assets/more-blue.svg";
import { ReactComponent as MoreIconDark } from "./assets/more-dark.svg";
import { ReactComponent as MoreIconLight } from "./assets/more-light.svg";

import { ReactComponent as CreateBoardIconBlue } from "./assets/create-board-blue.svg";
import { ReactComponent as CreateBoardIconDark } from "./assets/create-board-dark.svg";
import { ReactComponent as CreateBoardIconLight } from "./assets/create-board-light.svg";

import { ReactComponent as AddIconBlue } from "./assets/add-blue.svg";
import { ReactComponent as AddIconDark } from "./assets/add-dark.svg";
import { ReactComponent as AddIconLight } from "./assets/add-light.svg";

import { ReactComponent as PersonalizeMenuIconBlue } from "./assets/personalize-menu-blue.svg";
import { ReactComponent as PersonalizeMenuIconDark } from "./assets/personalize-menu-dark.svg";
import { ReactComponent as PersonalizeMenuIconLight } from "./assets/personalize-menu-light.svg";

import { PersonalisedMenuPopup } from "./components/PersonalisedMenuPopup";
import { PersonalizeMenuDialog } from "./components/PersonalizeMenuDialog/PersonalizeMenuDialog";

export const HomeSidebar: FC = () => {
  const newBoardDialogRef = useRef<NewBoardDialogHandle | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [
    isNewBoardFromTemplateDialogOpen,
    setIsNewBoardFromTemplateDialogOpen,
  ] = useState(false);
  const [isPersonalizeMenuDialogOpen, setIsPersonalizeMenuDialogOpen] =
    useState(false);

  const { entity, entitiesList: list } = useEntitySelectedProvider();
  const { hasEmployeeLikeRole, isMember } = useUserRole();

  const { navigateToPage } = useNavigation();
  const { me } = useMe();
  const { t } = useTranslations();
  const { tcx, customTheme } = useTheme();
  const isOverlayMode = useIsLargeOrBelow();
  const { isOverlayOpen, closeOverlay } = useSidebarNav();
  const currentPageNavConfig = useCurrentPageNavConfig();

  const { isOpen: isFeaturesShowcaseModalOpen } = useFeaturesShowcaseModal();

  const {
    unPinnedTools,
    pinnedTools,
    taskBoardSidebarItems,
    isSidebarExpanded,
    sidebarWidth,
    debugItem,
    updateIsSidebarExpanded,
    updateSidebarWidth,
  } = useHomeSidebar();

  const isExpanded = isOverlayMode ? true : isSidebarExpanded;
  const panelWidth = isOverlayMode
    ? SIDEBAR_CONSTANTS.DEFAULT_EXPANDED_WIDTH
    : sidebarWidth;

  const { isLoading: isBoardsLoading } = useTaskBoardsQuery({
    entityId: entity.id,
  });

  const {
    isEntitiesUpdatesPopupOpen,
    lastUpdatedEntityId,
    onCloseEntitiesUpdatesPopup,
  } = useEntitiesUpdatesPopup({ entityId: entity?.id });

  const { isActive: isSpotlightActive, markAsSeen: markSpotlightAsSeen } =
    useSpotlight({
      id: CREATE_BOARD_SPOTLIGHT_KEY,
      delay: 200,
    });

  const hasTaskBoardsAccess =
    !!entity && hasRoleAccessToModule(entity.role, AppModule.tasks);
  const canCreateTaskBoard = !hasEmployeeLikeRole && !isMember;

  const renderSidebarItems = useCallback(
    (items: SidebarItem[], expanded: boolean) =>
      items.map((item) => (
        <SidebarItemComponent
          key={item.title}
          item={item}
          isExpanded={expanded}
          onSideBarExpand={() => {
            if (isOverlayMode) {
              return;
            }
            updateSidebarWidth(expanded ? 80 : 210);
          }}
          itemsRenderer={renderSidebarItems}
        />
      )),
    [updateSidebarWidth, isOverlayMode]
  );

  const filteredBoardSidebarItems = taskBoardSidebarItems.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const moreNotificationsCount = useMemo(
    () =>
      unPinnedTools.reduce((total, item) => total + (item.unreadCount || 0), 0),
    [unPinnedTools]
  );

  const renderSidebarPanel = (isResizing: boolean) => (
    <div
      className={tcx(
        "group/homeSideBar relative flex h-full flex-col overflow-x-hidden border-0 border-r-[1px] border-solid border-[#D6D6DE] transition-[width] duration-300",
        {
          blue: "bg-primary-700",
          dark: "bg-[#333333]",
          light: "bg-white",
        },
        isOverlayMode && "border-r-0",
        isResizing && "transition-none"
      )}
      style={{ width: `${panelWidth}px` }}
    >
      <div className="relative flex shrink-0 flex-col">
        {!isOverlayMode && !isResizing && (
          <InteractiveContainer
            className={tcx(
              "fixed top-[48px] z-[49] flex h-6 w-6 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[1.5px] border-solid border-white bg-primary-600 text-white opacity-0 transition-all duration-300 group-hover/homeSideBar:opacity-100",
              isExpanded && "-rotate-180",
              {
                blue: "bg-primary-600",
                dark: "bg-[#3E3F40]",
                light: "bg-[#42A4F5]",
              }
            )}
            onClick={() => {
              markSpotlightAsSeen();
              updateSidebarWidth(isSidebarExpanded ? 80 : 210);
            }}
            style={{ left: `${panelWidth - 24}px` }}
          >
            <ChevronRightIcon />
          </InteractiveContainer>
        )}
      </div>
      <div
        className={tcx(
          "mr-[1px] flex grow flex-col gap-0.5 overflow-x-hidden overflow-y-auto px-2.5 pt-3 pb-4",
          isExpanded ? "items-start" : "items-center",
          styles.homeSideBarScrollbar,
          {
            blue: styles.homeSideBarScrollbarBlue,
            dark: styles.homeSideBarScrollbarDark,
            light: styles.homeSideBarScrollbarLight,
          }[customTheme]
        )}
      >
        {renderSidebarItems(
          pinnedTools.filter((item): item is SidebarItem => item !== undefined),
          isExpanded
        )}

        <Popover
          placement={isOverlayMode ? "right-start" : "left-end"}
          className={tcx(
            "w-[210px] bg-primary-700 text-primary-50 drop-shadow-[0_4px_32px_rgba(0,0,0,0.18)]",
            {
              blue: "bg-primary-700",
              dark: "bg-[#333333]",
              light: "bg-white",
            }
          )}
          renderTrigger={({ Trigger, triggerRef }) => (
            <Trigger
              ref={triggerRef}
              className={tcx(
                "z-10 m-0 border-none bg-transparent p-0 outline-none",
                { "w-full": isExpanded }
              )}
            >
              <SidebarItemComponent
                item={{
                  id: "more",
                  itemKey: "_more",
                  SvgIcon: {
                    blue: MoreIconBlue,
                    dark: MoreIconDark,
                    light: MoreIconLight,
                  }[customTheme],
                  title: t({
                    id: "home-sidebar-component.more",
                    defaultMessage: "More",
                  }),
                  type: "button",
                  onClick: () => {},
                  unreadStyle: "dot",
                  unreadCount: moreNotificationsCount,
                }}
                isExpanded={isExpanded}
                onSideBarExpand={() => updateIsSidebarExpanded(true)}
              />
            </Trigger>
          )}
        >
          {({ onClose }) => (
            <div className="flex flex-col gap-0.5 p-2.5">
              {!!unPinnedTools.length && (
                <>
                  {unPinnedTools.map((item) => (
                    <SidebarItemComponent
                      key={item.title}
                      item={item}
                      isExpanded
                      onSideBarExpand={() => updateIsSidebarExpanded(true)}
                      itemsRenderer={renderSidebarItems}
                      onLinkClick={onClose}
                    />
                  ))}
                  <div className="py-2.5">
                    <Divider />
                  </div>
                </>
              )}
              <SidebarItemComponent
                item={{
                  id: "personalise-menu",
                  itemKey: "_personaliseMenu",
                  SvgIcon: {
                    blue: PersonalizeMenuIconBlue,
                    dark: PersonalizeMenuIconDark,
                    light: PersonalizeMenuIconLight,
                  }[customTheme],
                  title: t({
                    id: "home-sidebar-component.personalise-menu",
                    defaultMessage: "Personalise Menu",
                  }),
                  type: "button",
                  onClick: () => setIsPersonalizeMenuDialogOpen(true),
                }}
                isExpanded
                onSideBarExpand={() => updateIsSidebarExpanded(true)}
                itemsRenderer={renderSidebarItems}
              />
              {debugItem && (
                <SidebarItemComponent
                  key={debugItem.id}
                  item={debugItem}
                  isExpanded
                  onSideBarExpand={() => updateIsSidebarExpanded(true)}
                  itemsRenderer={renderSidebarItems}
                  onLinkClick={onClose}
                />
              )}
            </div>
          )}
        </Popover>
        {hasTaskBoardsAccess && (
          <>
            <div className="mx-[-10px] w-[calc(100%+20px)] py-4">
              <Divider />
            </div>
            {canCreateTaskBoard && (
              <SpotlightTooltip
                isOpen={!isFeaturesShowcaseModalOpen && isSpotlightActive}
                onDismiss={markSpotlightAsSeen}
                placement="right-end"
                className="z-[20]"
                tip={t({
                  id: "home-sidebar-component.tap-to-create-new-task-board",
                  defaultMessage: "Tap here to create new Tasks Board ✨",
                })}
                showButton
                renderTrigger={({ ref, props }) => (
                  <div
                    ref={ref}
                    className={isExpanded ? "w-full" : ""}
                    {...props}
                  >
                    <Menu
                      placement="bottom"
                      className="ml-[40px]"
                      autoClose
                      renderTrigger={({ Trigger, triggerRef }) => (
                        <Trigger as={InteractiveContainer} ref={triggerRef}>
                          <SidebarLinkButton
                            className={isExpanded ? "w-full" : ""}
                            icon={
                              {
                                light: <AddIconLight />,
                                dark: <AddIconDark />,
                                blue: <AddIconBlue />,
                              }[customTheme]
                            }
                            startSlot={
                              {
                                light: <CreateBoardIconLight />,
                                dark: <CreateBoardIconDark />,
                                blue: <CreateBoardIconBlue />,
                              }[customTheme]
                            }
                            text={t({
                              id: "home-sidebar-component.boards",
                              defaultMessage: "Boards",
                            })}
                            isSidebarExpanded={isExpanded}
                            tooltip={
                              !isSpotlightActive
                                ? t({
                                    id: "home-sidebar-component.create-board",
                                    defaultMessage: "Create Board",
                                  })
                                : undefined
                            }
                          />
                        </Trigger>
                      )}
                      sections={[
                        [
                          {
                            id: "start-with-template",
                            label: t({
                              id: "home-sidebar-component.create-board-section-1",
                              defaultMessage: "Start with Template",
                            }),
                            icon: <CreateBoardFromTemplateIcon />,
                            onSelect: () => {
                              setIsNewBoardFromTemplateDialogOpen(true);
                            },
                          },
                          {
                            id: "create-from-scratch",
                            label: t({
                              id: "home-sidebar-component.create-board-section-2",
                              defaultMessage: "Create from Scratch",
                            }),
                            icon: <CreateBoardFromScratchIcon />,
                            onSelect: () => {
                              markSpotlightAsSeen();
                              newBoardDialogRef.current?.open();
                            },
                          },
                        ],
                      ]}
                    />
                  </div>
                )}
              />
            )}
            {!!taskBoardSidebarItems.length && isExpanded && (
              <div className="!max-w-[100%] py-2">
                <ExpandableSearchBar
                  color="white"
                  size="lg"
                  isAlwaysExpanded
                  defaultValue={searchQuery}
                  onSearch={(e) => setSearchQuery(e)}
                  expandedClassName="!max-w-full w-full"
                  className={tcx("!w-full min-w-[100%] px-0 font-secondary", {
                    light:
                      "bg-[#F4FAFE] [&_*]:fill-[#6192BE] [&_path]:fill-[#6192BE] [&_svg]:fill-[#6192BE] [&>*]:!text-dark [&>*]:placeholder-dark-700",
                    dark: "bg-[#3D3D3D] [&_*]:fill-white [&_path]:fill-white [&_svg]:fill-white [&>*]:!text-white [&>*]:placeholder-dark-200",
                    blue: "bg-[#2F7DDA] [&_*]:fill-white [&_path]:fill-white [&_svg]:fill-white [&>*]:!text-white [&>*]:placeholder-primary-50",
                  })}
                  searchIconClassName="left-2.5"
                  clearClassName="hover:bg-transparent"
                  endSlotContainerClassName="right-1.5"
                />
              </div>
            )}
            {filteredBoardSidebarItems.map((item) => (
              <SidebarItemComponent
                key={item.id}
                item={item}
                isExpanded={isExpanded}
                onSideBarExpand={() => updateIsSidebarExpanded(true)}
              />
            ))}
            {isBoardsLoading && <BoardListSkeleton />}
            {isExpanded &&
              searchQuery &&
              filteredBoardSidebarItems.length === 0 && (
                <div
                  className={tcx("w-full text-center", {
                    blue: "text-primary-50",
                    dark: "text-white",
                    light: "text-dark-800",
                  })}
                >
                  {t({
                    id: "common.no-results",
                    defaultMessage: "No results",
                  })}
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );

  const renderSidebar = () => {
    if (!isOverlayMode && currentPageNavConfig?.isSideBarHidden) {
      return null;
    }

    if (isOverlayMode) {
      return (
        <SidebarOverlay
          isOpen={isOverlayOpen}
          closeLabel={t({
            id: "home-sidebar-component.close-navigation",
            defaultMessage: "Close navigation",
          })}
          onClose={closeOverlay}
        >
          {renderSidebarPanel(false)}
        </SidebarOverlay>
      );
    }

    return (
      <MultiSectionLayout>
        <ResizableSidebarWrapper
          sidebarWidth={sidebarWidth}
          onWidthChange={updateSidebarWidth}
        >
          {(isResizing) => renderSidebarPanel(isResizing)}
        </ResizableSidebarWrapper>
      </MultiSectionLayout>
    );
  };

  return (
    <>
      {renderSidebar()}
      <EntitiesUpdatesPopup
        isOpen={isEntitiesUpdatesPopupOpen}
        entities={list}
        lastUpdatedEntityId={lastUpdatedEntityId}
        onClose={onCloseEntitiesUpdatesPopup}
      />
      <PersonalizeMenuDialog
        isOpen={isPersonalizeMenuDialogOpen}
        onClose={() => setIsPersonalizeMenuDialogOpen(false)}
      />
      <BoardTemplatesModal
        entityId={entity?.id || ""}
        isOpen={isNewBoardFromTemplateDialogOpen}
        onUseTemplateSuccess={(id: string) => {
          navigateToPage("tasksTasks", { boardId: id });
        }}
        onClose={() => setIsNewBoardFromTemplateDialogOpen(false)}
      />
      <PersonalisedMenuPopup
        onPersonaliseMenuDialogOpen={() => setIsPersonalizeMenuDialogOpen(true)}
      />
      {entity && me?.id && (
        <NewBoardDialog
          entityId={entity.id}
          meId={me.id}
          ref={newBoardDialogRef}
          onAfterCreate={(boardId) => navigateToPage("tasksTasks", { boardId })}
        />
      )}
    </>
  );
};
